import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import breakpoint from "styled-components-breakpoint";

// 滚动容器样式
const ScrollView = styled.div`
  overflow-x: scroll;
  width: 100%;
  display: flex;
  align-items: center;
  scroll-behavior: smooth;

  > div {
    flex-shrink: 0;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  ${breakpoint("desktop")`
    width: 1136px;
  `}
`;

// 自动滚动水平滚动组件
const AutoScrollingScrollView = ({ children, speed = 4 }) => {
  const scrollRef = useRef(null);
  const animationRef = useRef(null);
  const lastDirectionRef = useRef(1); // 1 = 向右, -1 = 向左
  const isUserInteractingRef = useRef(false);
  const userScrollTimeoutRef = useRef(null);

  // 自动滚动逻辑
  const autoScroll = () => {
    if (!scrollRef.current || isUserInteractingRef.current) return;

    const container = scrollRef.current;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;
    const maxScroll = scrollWidth - clientWidth;

    // 如果内容宽度小于容器宽度，不需要滚动
    if (maxScroll <= 0) return;

    let newScrollLeft = container.scrollLeft + lastDirectionRef.current * speed;

    // 检查边界
    if (newScrollLeft <= 0) {
      newScrollLeft = 0;
      lastDirectionRef.current = 1; // 向右滚动
    } else if (newScrollLeft >= maxScroll) {
      newScrollLeft = maxScroll;
      lastDirectionRef.current = -1; // 向左滚动
    }

    // 更新滚动位置
    container.scrollLeft = newScrollLeft;

    // 继续动画
    animationRef.current = requestAnimationFrame(autoScroll);
  };

  // 用户交互开始
  const handleInteractionStart = () => {
    isUserInteractingRef.current = true;
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    // 清除之前的超时
    if (userScrollTimeoutRef.current) {
      clearTimeout(userScrollTimeoutRef.current);
    }
  };

  // 用户交互结束
  const handleInteractionEnd = () => {
    // 设置超时以检测用户交互是否真的结束
    userScrollTimeoutRef.current = setTimeout(() => {
      isUserInteractingRef.current = false;
      animationRef.current = requestAnimationFrame(autoScroll);
    }, 500);
  };

  // 处理用户滚动方向
  const handleScroll = (e) => {
    if (isUserInteractingRef.current) {
      // 检测滚动方向
      if (e.deltaY > 0 || e.deltaX > 0) {
        lastDirectionRef.current = 1; // 向右
      } else if (e.deltaY < 0 || e.deltaX < 0) {
        lastDirectionRef.current = -1; // 向左
      }
    }
  };

  // 初始化和清理
  useEffect(() => {
    // 开始自动滚动
    animationRef.current = requestAnimationFrame(autoScroll);

    // 添加事件监听器
    const container = scrollRef.current;
    if (container) {
      container.addEventListener("wheel", handleScroll);
      container.addEventListener("mouseenter", handleInteractionStart);
      container.addEventListener("mouseleave", handleInteractionEnd);
      container.addEventListener("touchstart", handleInteractionStart);
      container.addEventListener("touchend", handleInteractionEnd);
    }

    return () => {
      // 清理
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }

      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }

      if (container) {
        container.removeEventListener("wheel", handleScroll);
        container.removeEventListener("mouseenter", handleInteractionStart);
        container.removeEventListener("mouseleave", handleInteractionEnd);
        container.removeEventListener("touchstart", handleInteractionStart);
        container.removeEventListener("touchend", handleInteractionEnd);
      }
    };
  }, [speed]);

  return (
    <ScrollView
      ref={scrollRef}
      onMouseEnter={handleInteractionStart}
      onMouseLeave={handleInteractionEnd}
      onTouchStart={handleInteractionStart}
      onTouchEnd={handleInteractionEnd}
    >
      {children}
    </ScrollView>
  );
};

export default AutoScrollingScrollView;
